import type { NormalizedApiError } from '../core/normalizedApiError';
import { parseFastApiDetail } from './parseFastApiDetail';
import { parseProblemDetailsJson } from './parseProblemDetails';

/**
 * Parse axios error `response.data` (or top-level problem JSON) into `NormalizedApiError`.
 */
export function parseAxiosApiError(error: unknown): NormalizedApiError | null {
  const err = error as { response?: { status?: number; data?: unknown } };
  const status = err?.response?.status;
  const data = err?.response?.data;
  return parseResponseData(data, status);
}

export function parseResponseData(data: unknown, httpStatus?: number): NormalizedApiError | null {
  const fast = parseFastApiDetail(data, httpStatus);
  if (fast) return fast;

  const prob = parseProblemDetailsJson(data, httpStatus);
  if (prob) return prob;

  return null;
}

/**
 * Attach `userFacingMessage` on axios errors for consumers that read `error.userFacingMessage`.
 */
export function attachUserFacingMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): void {
  if (typeof error !== 'object' || error === null) return;
  const parsed = parseAxiosApiError(error);
  const msg = parsed?.message || (error as { message?: string }).message || fallback;
  (error as { userFacingMessage?: string }).userFacingMessage =
    typeof msg === 'string' && msg.trim() ? msg : fallback;
}

export function getUserFacingMessage(error: unknown, fallback?: string): string {
  const withUser = error as { userFacingMessage?: string };
  if (typeof withUser?.userFacingMessage === 'string' && withUser.userFacingMessage.trim()) {
    return withUser.userFacingMessage;
  }
  const parsed = parseAxiosApiError(error);
  if (parsed?.message) return parsed.message;
  const m = (error as { message?: string })?.message;
  if (typeof m === 'string' && m.trim()) return m;
  return fallback ?? 'Something went wrong. Please try again.';
}

export function getErrorCodeFromAxios(error: unknown): string | undefined {
  const parsed = parseAxiosApiError(error);
  if (parsed?.errorCode) return parsed.errorCode;
  const err = error as { response?: { data?: { error_code?: string } } };
  const top = err?.response?.data;
  if (top && typeof top === 'object' && typeof (top as { error_code?: string }).error_code === 'string') {
    return (top as { error_code: string }).error_code;
  }
  return undefined;
}
